import { FormEvent, useState } from "react";
import { isAdminError, UsageItem, UsageMetric } from "../../webinars/types";
import { webinarRepository } from "../../webinars/repository";
import { useRepositoryData } from "../../webinars/useRepositoryData";
import { formatDateTime, formatShortDate } from "../../webinars/format";
import { SUPABASE_USAGE_URL } from "../../webinars/config";

/** From here a bar turns amber */
const WARNING_SHARE = 0.8;
/** Hand-entered figures older than this are flagged as needing an update */
const STALE_DAYS = 7;

/** Decimal units, as Supabase's plan uses: "27.4 MB", "1.20 GB" */
export function formatBytes(bytes: number) {
  if (bytes < 1e6) return `${Math.round(bytes / 1e3)} KB`;
  if (bytes < 1e9) return `${(bytes / 1e6).toFixed(bytes < 1e7 ? 1 : 0)} MB`;
  return `${(bytes / 1e9).toFixed(2)} GB`;
}

const formatValue = (item: UsageItem, value: number) =>
  item.unit === "bytes" ? formatBytes(value) : value.toLocaleString("en-ZA");

const shareOf = (item: UsageItem) => (item.used === null ? 0 : item.used / item.limit);
const toneOf = (item: UsageItem) =>
  item.used === null ? "none" : shareOf(item) >= 1 ? "full" : shareOf(item) >= WARNING_SHARE ? "warning" : "ok";
const daysSince = (iso: string) => Math.floor((Date.now() - Date.parse(iso)) / 864e5);

function UsageRow({ item, notify }: { item: UsageItem; notify: (message: string) => void }) {
  const [editing, setEditing] = useState(false);
  const [amount, setAmount] = useState("");
  const [unit, setUnit] = useState<"MB" | "GB">("GB");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const tone = toneOf(item);
  const percent = Math.round(shareOf(item) * 100);
  const stale = item.source === "manual" && item.recordedAt && daysSince(item.recordedAt) > STALE_DAYS;
  const growth = item.used !== null && item.previous && item.recordedAt && item.previous.recordedAt < item.recordedAt
    ? item.used - item.previous.value
    : 0;

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    // Accepts "1.5" or "1,5"
    const number = Number(amount.trim().replace(",", "."));
    if (!amount.trim() || !Number.isFinite(number) || number < 0) {
      setError("Enter the figure shown in Supabase, e.g. 0.42");
      return;
    }
    setSaving(true);
    try {
      await webinarRepository.recordProjectUsage(item.metric, Math.round(number * (unit === "GB" ? 1e9 : 1e6)));
      notify(`${item.label} recorded`);
      setEditing(false);
      setAmount("");
    } catch (err) {
      setError(isAdminError(err) ? err.message : "Couldn't save it. Check your connection and try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <li className={`wa-usage-row wa-usage-row--${tone}`}>
      <div className="wa-usage-head">
        <span className="wa-usage-label">{item.label}</span>
        <span className="wa-usage-figures">
          {item.used === null
            ? <span className="wa-muted">Not recorded yet · limit {formatValue(item, item.limit)}</span>
            : <><strong>{formatValue(item, item.used)}</strong> of {formatValue(item, item.limit)} · {percent}%</>}
        </span>
      </div>
      <div
        className="wa-quota-bar"
        role="meter"
        aria-label={item.label}
        aria-valuemin={0}
        aria-valuemax={item.limit}
        aria-valuenow={Math.min(item.used ?? 0, item.limit)}
      >
        <div style={{ width: `${Math.min(100, percent)}%` }} />
      </div>
      <p className="wa-usage-meta">
        {item.source === "measured"
          ? <>Measured {item.recordedAt ? formatDateTime(item.recordedAt) : "when this page opened"} SAST</>
          : item.recordedAt
            ? <>Entered {formatDateTime(item.recordedAt)} SAST from Supabase's usage page</>
            : <>Copy this figure from Supabase's usage page</>}
        {growth > 0 && item.previous && <> · +{formatValue(item, growth)} since {formatShortDate(item.previous.recordedAt)}</>}
        {stale && <span className="wa-usage-stale"> · {daysSince(item.recordedAt!)} days old: update it</span>}
        {item.source === "manual" && !editing && (
          <> · <button type="button" className="wa-link wa-link--inline" onClick={() => setEditing(true)}>Update<span className="km-visuallyHidden"> {item.label}</span></button></>
        )}
      </p>
      {editing && (
        <form className="wa-usage-form" onSubmit={save}>
          <label className="wa-field">
            {item.label} now, as shown in Supabase
            <span className="wa-usage-input">
              <input type="text" inputMode="decimal" placeholder="e.g. 0.42" value={amount} onChange={e => setAmount(e.target.value)} autoFocus />
              <select value={unit} onChange={e => setUnit(e.target.value as "MB" | "GB")} aria-label="Unit">
                <option value="GB">GB</option>
                <option value="MB">MB</option>
              </select>
            </span>
          </label>
          <div className="wa-row-actions">
            <button type="submit" className="wa-btn" disabled={saving}>{saving ? "Saving…" : "Save"}</button>
            <button type="button" className="wa-link" onClick={() => { setEditing(false); setError(""); }}>Cancel</button>
          </div>
          {error && <p className="wa-form-error" role="alert">{error}</p>}
        </form>
      )}
    </li>
  );
}

/** Short names for the summary grid */
const SHORT_LABELS: Record<UsageMetric, string> = {
  database: "Database",
  storage: "File storage",
  egress: "Egress",
  mau: "Active users",
  log_ingestion: "Log ingestion",
  log_query: "Log query",
};

// The Supabase project against its plan's limits (the free plan: 500 MB database, 1 GB file
// storage, 5 GB egress, 50,000 monthly active users, 1 GB log ingestion, 100 GB log query).
// Database size, file storage and monthly active users are measured each time this opens;
// egress and logs only show in Supabase, so they're copied from its usage page.
// A compact summary of all six; the full figures and the Update forms are under Details, which
// opens by itself when a figure is nearly at or over its limit.
export default function ProjectUsagePanel({ notify }: { notify: (message: string) => void }) {
  const { data, loading, error } = useRepositoryData(() => webinarRepository.getProjectUsage(), "project-usage");
  /** null until the admin opens or closes Details: until then it follows the figures */
  const [open, setOpen] = useState<boolean | null>(null);

  // The prototype has no Supabase project
  if (data === null) return null;

  const items = data ?? [];
  const over = items.filter(item => toneOf(item) === "full").length;
  const near = items.filter(item => toneOf(item) === "warning").length;
  const toUpdate = items.filter(item => item.source === "manual" && (!item.recordedAt || daysSince(item.recordedAt) > STALE_DAYS)).length;
  const tone = over ? "full" : near ? "warning" : "ok";
  const summary = loading && !data ? "Measuring…"
    : over > 0 ? `${over} over the limit`
    : near > 0 ? `${near} nearly at the limit`
    : "Within the free plan";
  const expanded = open ?? (over + near > 0);

  return (
    <section className={`wa-panel wa-monitor wa-usage wa-usage--${tone}`} aria-labelledby="wa-usage-title">
      <div className="wa-monitor-head">
        <h2 id="wa-usage-title">Supabase usage</h2>
        <span className={`wa-monitor-status wa-monitor-status--${error ? "none" : tone}`}>{error ? "Not loaded" : summary}</span>
      </div>

      {error ? (
        <p className="wa-form-error" role="alert">
          {isAdminError(error) ? error.message : "Couldn't load the usage figures. Reload the page to try again."}
        </p>
      ) : (
        <ul className="wa-usage-mini" aria-label="Free plan limits used">
          {items.map(item => (
            <li key={item.metric} className={`wa-usage-mini-item wa-usage-row--${toneOf(item)}`}>
              <span className="wa-usage-mini-label">{SHORT_LABELS[item.metric]}</span>
              <strong>{item.used === null ? "–" : `${Math.round(shareOf(item) * 100)}%`}</strong>
              <div className="wa-quota-bar" aria-hidden="true">
                <div style={{ width: `${Math.min(100, Math.round(shareOf(item) * 100))}%` }} />
              </div>
            </li>
          ))}
        </ul>
      )}

      {toUpdate > 0 && !error && (
        <p className="wa-usage-stale wa-usage-reminder">
          {toUpdate} figure{toUpdate === 1 ? "" : "s"} to copy from Supabase: open Details to update {toUpdate === 1 ? "it" : "them"}.
        </p>
      )}

      <button type="button" className="wa-link wa-monitor-toggle" aria-expanded={expanded} aria-controls="wa-usage-details" onClick={() => setOpen(!expanded)}>
        {expanded ? "Hide details" : "Details"}<span aria-hidden="true">{expanded ? " ▴" : " ▾"}</span>
      </button>
      {expanded && (
        <div id="wa-usage-details" className="wa-monitor-details">
          {!error && (
            <ul className="wa-usage-list">
              {items.map(item => <UsageRow key={item.metric} item={item} notify={notify} />)}
            </ul>
          )}
          <a className="wa-btn" href={SUPABASE_USAGE_URL} target="_blank" rel="noreferrer">Open Supabase usage page ↗</a>
          <p className="wa-muted wa-usage-note">
            Database size, file storage and monthly active users are measured live each time this page opens; Supabase's own
            figures update once a day, so they can differ a little. Database size is your data, tables and indexes: the figure
            the 500 MB limit applies to (Supabase's larger "disk size" also counts system files). Egress and log
            figures only show in Supabase: copy them from its usage page about once a week. Free projects are paused after a
            week without activity, which stops registrations until the project is restored in Supabase.
          </p>
        </div>
      )}
    </section>
  );
}
